/**
 * Call-server (ICE) configuration: relay server URL/credentials or hosted
 * provider account. Secrets are write-only (blank = keep current value).
 */

import { useEffect, useState } from 'react';
import { useNotify, useTranslate } from 'react-admin';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  MenuItem,
  TextField,
} from '@mui/material';
import { httpClient } from '@/utils/httpClient';
import type { ApiEnvelope } from '@/types/api';

interface CallServerDetails {
  id: number;
  url?: string | null;
  username?: string | null;
  servertype?: string | null;
  twilio_account_sid?: string | null;
}

export const CallServerForm = () => {
  const translate = useTranslate();
  const notify = useNotify();
  const [recordId, setRecordId] = useState<number | null>(null);
  const [loaded, setLoaded] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    servertype: '',
    url: '',
    username: '',
    password: '',
    twilio_account_sid: '',
    twilio_auth_token: '',
  });

  useEffect(() => {
    httpClient
      .get<ApiEnvelope<CallServerDetails>>('/serverDetails')
      .then((response) => {
        const data = response.data.data;
        if (data) {
          setRecordId(data.id);
          setForm((prev) => ({
            ...prev,
            servertype: data.servertype ?? 'custom',
            url: data.url ?? '',
            username: data.username ?? '',
            twilio_account_sid: data.twilio_account_sid ?? '',
          }));
        }
        setLoaded(true);
      })
      .catch(() => setLoaded(true));
  }, []);

  const set = (key: string) => (event: React.ChangeEvent<HTMLInputElement>) =>
    setForm((prev) => ({ ...prev, [key]: event.target.value }));

  const handleSave = async () => {
    setSaving(true);
    try {
      const body: Record<string, string> = {
        servertype: form.servertype,
        url: form.url,
        username: form.username,
        twilio_account_sid: form.twilio_account_sid,
      };
      // Secrets: only send when the admin typed a new value
      if (form.password) body.password = form.password;
      if (form.twilio_auth_token) body.twilio_auth_token = form.twilio_auth_token;
      const response = await httpClient.patch<ApiEnvelope>(
        `/serverDetails/${recordId ?? 'insert'}`,
        body
      );
      if (response.data.status === 'error') {
        notify(response.data.message || 'ra.notification.http_error', { type: 'error' });
      } else {
        notify('pages.serverSettings.saved', { type: 'info' });
        setForm((prev) => ({ ...prev, password: '', twilio_auth_token: '' }));
      }
    } catch (error: any) {
      notify(error?.message || 'ra.notification.http_error', { type: 'error' });
    } finally {
      setSaving(false);
    }
  };

  if (!loaded) return <CircularProgress size={28} />;

  const hosted = form.servertype === 'twilioServer';

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Alert severity="info">{translate('pages.serverSettings.secret_note')}</Alert>
      <TextField
        select
        label={translate('pages.serverSettings.callserver.servertype')}
        value={form.servertype}
        onChange={set('servertype')}
      >
        <MenuItem value="custom">
          {translate('pages.serverSettings.callserver.type_custom')}
        </MenuItem>
        <MenuItem value="twilioServer">
          {translate('pages.serverSettings.callserver.type_hosted')}
        </MenuItem>
      </TextField>
      {!hosted && (
        <>
          <TextField
            label={translate('pages.serverSettings.callserver.url')}
            value={form.url}
            onChange={set('url')}
          />
          <TextField
            label={translate('pages.serverSettings.callserver.username')}
            value={form.username}
            onChange={set('username')}
          />
          <TextField
            type="password"
            autoComplete="new-password"
            label={translate('pages.serverSettings.callserver.password')}
            placeholder={translate('pages.serverSettings.secret_placeholder')}
            value={form.password}
            onChange={set('password')}
          />
        </>
      )}
      {hosted && (
        <>
          <TextField
            label={translate('pages.serverSettings.callserver.account_sid')}
            value={form.twilio_account_sid}
            onChange={set('twilio_account_sid')}
          />
          <TextField
            type="password"
            autoComplete="new-password"
            label={translate('pages.serverSettings.callserver.auth_token')}
            placeholder={translate('pages.serverSettings.secret_placeholder')}
            value={form.twilio_auth_token}
            onChange={set('twilio_auth_token')}
          />
        </>
      )}
      <Button variant="contained" onClick={handleSave} disabled={saving} sx={{ width: 'fit-content' }}>
        {translate('ra.action.save')}
      </Button>
    </Box>
  );
};

export default CallServerForm;
